import { Link } from "react-router-dom";
import { Flame, Heart } from "lucide-react";
import { motion } from "framer-motion";
import type { Book } from "@/data/books";
import { useUserBooks } from "@/hooks/useUserBooks";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const SpiceLevel = ({ level }: { level: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3].map((i) => (
      <Flame
        key={i}
        className={`w-3.5 h-3.5 ${i <= level ? "text-primary fill-primary" : "text-muted-foreground/30"}`}
      />
    ))}
  </div>
);

const BookCard = ({ book }: { book: Book }) => {
  const { user } = useAuth();
  const { statusFor, setStatus, remove } = useUserBooks();
  const saved = statusFor(book.id) === "tbr";

  const onSaveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to save books");
      return;
    }
    if (saved) remove(book.id);
    else setStatus(book.id, "tbr");
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="group"
    >
      <Link to={`/book/${book.id}`} className="block">
        <div
          className={`aspect-[2/3] rounded-xl bg-gradient-to-br ${book.coverColor} relative overflow-hidden mb-3 shadow-sm group-hover:shadow-xl transition-shadow`}
        >
          {book.coverImageUrl && (
            <img
              src={book.coverImageUrl}
              alt={`${book.title} cover`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          )}
          <div className="absolute top-3 right-3 bg-background/70 backdrop-blur-sm rounded-full px-1.5 py-1">
            <SpiceLevel level={book.spiceLevel} />
          </div>
          <button
            type="button"
            onClick={onSaveClick}
            aria-label={saved ? "Remove from saved" : "Save book"}
            className={`absolute top-3 left-3 rounded-full p-2 backdrop-blur-sm transition-colors ${
              saved
                ? "bg-primary text-primary-foreground"
                : "bg-background/70 text-foreground hover:bg-primary/30"
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${saved ? "fill-current" : ""}`} />
          </button>
        </div>
        <div className="space-y-1 px-0.5">
          <h3 className="font-display text-base font-bold text-foreground leading-snug line-clamp-2">
            {book.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-1">{book.author}</p>
          <div className="flex flex-wrap gap-1.5 pt-1.5">
            {(book.subgenre ?? []).slice(0, 2).map((sg) => (
              <span key={`sg-${sg}`} className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                {sg}
              </span>
            ))}
            {book.tropes.slice(0, 2).map((trope) => (
              <span key={trope} className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
                {trope}
              </span>
            ))}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default BookCard;
